/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn, IDetailsRowProps, Icon, SelectionMode } from '@fluentui/react';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { Table } from 'src/core/components/Table';
import { SortKeyType, TableProps } from 'src/core/components/TableProps';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { EntityStore } from 'src/core/types/EntityStore';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { CustomTextTestSetDetailsTableRow } from 'src/customText/appShell/modelDetailsPages/tabs/CustomTextTestSetDetailsTab';
import { Row } from 'src/luis/projects/analytics/components/TrainingEvaluation/Legend/LegendRowHelpers';
import styled from 'styled-components';

export type CustomTextTestSetDetailsTableProps = TableProps<CustomTextTestSetDetailsTableRow> & {
    isLoading?: boolean;
    emptyPlaceholder: string;
    isExtractionProject: boolean;
    onRowClick: (index: number) => void;
    entityStores: EntityStore[];
    onSortColumn: (column: IColumn) => void;
    items: CustomTextTestSetDetailsTableRow[];
    sortKey: SortKeyType<CustomTextTestSetDetailsTableRow>;
};

const messages = defineMessages({
    documentName: {
        id: 'customText.customTextTestSetDetailsTable.documentName',
        defaultMessage: 'Document name'
    },
    labeledAs: {
        id: 'customText.customTextTestSetDetailsTable.labeledAs',
        defaultMessage: 'Labeled as'
    },
    predictedAs: {
        id: 'customText.customTextTestSetDetailsTable.predictedAs',
        defaultMessage: 'Predicted as'
    },
    text: {
        id: 'customText.customTextTestSetDetailsTable.text',
        defaultMessage: 'Labeled text'
    },
    conflictTooltip: {
        id: 'customText.customTextTestSetDetailsTable.conflictTooltip',
        defaultMessage: 'There are conflicts between labels and predictions'
    },
    noConflictTooltip: {
        id: 'customText.customTextTestSetDetailsTable.noConflictTooltip',
        defaultMessage: 'There are no conflicts between labels and predictions'
    },
    conflictIcon: {
        id: 'customText.customTextTestSetDetailsTable.conflictIcon',
        defaultMessage: 'Conflict icon'
    },
    noConflictIcon: {
        id: 'customText.customTextTestSetDetailsTable.noConflictIcon',
        defaultMessage: 'No conflict icon'
    },
    resultType: {
        id: 'customText.customTextTestSetDetailsTable.resultType',
        defaultMessage: 'Result type'
    }
});

const modalTheme = getScopedTheme('TestSetDetailsTable');

const ResultIcon = styled(Icon)<{ isTruePositive: boolean }>(props => ({
    marginRight: '10px',
    color: props.isTruePositive ? modalTheme.completedIconColor : modalTheme.warningIconColor
}));

export const CustomTextTestSetDetailsTable = observer((props: CustomTextTestSetDetailsTableProps) => {
    const { items, sortKey, isLoading, onRowClick, entityStores, onSortColumn, emptyPlaceholder, isExtractionProject, ...rest } = props;
    const { localize } = useLocalization();

    const columns: IColumn[] = [
        {
            minWidth: 200,
            maxWidth: 300,
            isRowHeader: true,
            isResizable: true,
            key: 'name',
            name: localize(messages.documentName),
            isSorted: sortKey?.key === 'name',
            isSortedDescending: sortKey?.key === 'name' && sortKey?.order === 'desc',
            onRender: (item: CustomTextTestSetDetailsTableRow) => <TruncatedText title={item.name}>{item.name}</TruncatedText>
        },
        {
            minWidth: 200,
            maxWidth: 350,
            key: 'labelText',
            isResizable: true,
            fieldName: 'labelText',
            name: localize(messages.labeledAs),
            isSorted: sortKey?.key === 'labelText',
            isSortedDescending: sortKey?.key === 'labelText' && sortKey?.order === 'desc',
            onRender: (item: CustomTextTestSetDetailsTableRow) => <TruncatedText title={item.labelText}>{item.labelText}</TruncatedText>
        },
        {
            minWidth: 200,
            maxWidth: 350,
            isResizable: true,
            key: 'predictionText',
            fieldName: 'predictionText',
            name: localize(messages.predictedAs),
            isSorted: sortKey?.key === 'predictionText',
            isSortedDescending: sortKey?.key === 'predictionText' && sortKey?.order === 'desc',
            onRender: (item: CustomTextTestSetDetailsTableRow) => (
                <TruncatedText title={item.predictionText}>{item.predictionText}</TruncatedText>
            )
        },

        {
            minWidth: 200,
            maxWidth: 250,
            key: 'resultType',
            isResizable: true,
            name: localize(messages.resultType),
            isSorted: sortKey?.key === 'resultType',
            isSortedDescending: sortKey?.key === 'resultType' && sortKey?.order === 'desc',
            onRender: (item: CustomTextTestSetDetailsTableRow) => (
                <LuisText>
                    <ResultIcon isTruePositive={item.isResultTruePositive} iconName={item.isResultTruePositive ? 'Completed' : 'warning'} />
                    {item.resultType}
                </LuisText>
            )
        }
    ];

    if (isExtractionProject) {
        columns.splice(1, 0, {
            minWidth: 200,
            maxWidth: 350,
            isResizable: true,
            key: 'text',
            fieldName: 'text',
            name: localize(messages.text),
            isSorted: sortKey?.key === 'text',
            isSortedDescending: sortKey?.key === 'text' && sortKey?.order === 'desc',
            onRender: (item: CustomTextTestSetDetailsTableRow) => <TruncatedText title={item.text}>{item.text}</TruncatedText>
        });
    }

    const onRenderRow = (defaultProps: IDetailsRowProps, defaultRenderer: (props: IDetailsRowProps) => JSX.Element) => (
        <Row
            key={defaultProps.itemIndex}
            style={{ cursor: 'pointer' }}
            onClick={_ => onRowClick(defaultProps.itemIndex)}
            onKeyPress={event => event.key === LuisKeyCodes.Enter && onRowClick(defaultProps.itemIndex)}>
            {defaultRenderer(defaultProps)}
        </Row>
    );

    return (
        <Table<CustomTextTestSetDetailsTableRow>
            items={items}
            columns={columns}
            isLoading={isLoading}
            onRenderRow={onRenderRow}
            onSortColumn={onSortColumn}
            emptyPlaceholder={emptyPlaceholder}
            selectionMode={SelectionMode.none}
            styles={{ root: { maxWidth: '1200px' } }}
            {...rest}
        />
    );
});
